import { Button, Col, Form, List, Typography } from "antd";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import { useEffect, useState } from "react";
import DynamicForm from "../../controls/DynamicForm";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import {
  getCompanyRoleLabels,
  addCompanyRoleLabel,
  deleteCompanyRoleLabel,
  getCompanyUserRoles,
} from "../../repository/data.repository";
import { CompanyRoleLabel, MAX_PAGE_SIZE } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { retry, getProblemMessage } from "../../utils/http";

export interface LabelRolesManagementProps {
  readonly labelUid: string;
  readonly setError: (message: string) => void;
}

/**
 * Manages a label's **company-role targeting** (the `LabelCompanyRole` junction) from the label's
 * perspective: pick a company role to add it, remove from the list. Targeting holders of these roles
 * is what label-based notification targeting (`NotificationTarget.Label`) resolves to. Add/remove
 * apply immediately (independent of the label's name/description save).
 */
function LabelRolesManagement(props: LabelRolesManagementProps) {
  const [roleForm] = Form.useForm();
  const [roles, setRoles] = useState<CompanyRoleLabel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.labelUid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.labelUid]);

  function load(): void {
    setLoading(true);
    retry(() =>
      getCompanyRoleLabels({
        searchQuery: {
          conditions: [
            { field: "labelUid", operation: QueryOperation.Equal, values: [props.labelUid] },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then((data) => {
        setRoles(data.data ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  function onAddRole(): void {
    const role = roleForm.getFieldValue("role");
    if (!role?.value) return;
    props.setError("");
    setLoading(true);
    addCompanyRoleLabel({ roleUid: role.value, labelUids: [props.labelUid] })
      .then(() => {
        roleForm.resetFields();
        load();
      })
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  function onRemoveRole(role: CompanyRoleLabel): void {
    props.setError("");
    setLoading(true);
    deleteCompanyRoleLabel(role.companyRoleUid, props.labelUid)
      .then(() => load())
      .catch((err) => {
        setLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  const rolesRequest = createSearchRequest(getCompanyUserRoles, "name");

  return (
    <Col className="flex min-w-0 flex-col">
      <Typography.Text strong className="mb-1 block">
        Target Company Roles
      </Typography.Text>
      <Typography.Text type="secondary" className="mb-2 block text-xs">
        Label-based targeting (e.g. notifications) reaches holders of these company roles.
      </Typography.Text>
      <DynamicForm
        form={roleForm}
        schema={{
          groups: [
            {
              controls: [
                {
                  type: "combobox",
                  name: "role",
                  label: "Add a company role",
                  request: rolesRequest,
                },
              ],
            },
          ],
        }}
        onChange={onAddRole}
      />
      <List
        size="small"
        loading={loading}
        dataSource={roles}
        locale={{ emptyText: "No roles targeted yet" }}
        renderItem={(role) => (
          <List.Item
            className="px-1"
            actions={[
              <Button
                key="remove"
                type="text"
                danger
                icon={<Icon className="text-xl" component={Icons.CloseIcon} />}
                onClick={() => onRemoveRole(role)}
              />,
            ]}
          >
            <Typography.Text className="truncate">
              {role.companyRoleName}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Col>
  );
}

export default LabelRolesManagement;
